export const TRUSTED_DEVICE_DAYS = 7;
export const TRUSTED_DEVICE_MS = TRUSTED_DEVICE_DAYS * 24 * 60 * 60 * 1000;

interface BrowserStorage {
  readonly length: number;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  key(index: number): string | null;
}

interface TrustedDeviceRecord {
  username: string;
  expiresAt: number;
}

const normalizedEmail = (email: string): string => email.trim().toLowerCase();

const trustKey = (clientId: string, email: string): string =>
  `iland.auth.trustedDevice.${clientId}.${encodeURIComponent(normalizedEmail(email))}`;

const cognitoDeviceKeys = (clientId: string, username: string): string[] => {
  const prefix = `CognitoIdentityServiceProvider.${clientId}.${username}`;
  return [`${prefix}.deviceKey`, `${prefix}.randomPasswordKey`, `${prefix}.deviceGroupKey`];
};

const parseRecord = (raw: string | null): TrustedDeviceRecord | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<TrustedDeviceRecord>;
    if (
      typeof parsed.username !== 'string' ||
      !parsed.username ||
      parsed.username.length > 256 ||
      typeof parsed.expiresAt !== 'number' ||
      !Number.isFinite(parsed.expiresAt)
    ) {
      return null;
    }
    return { username: parsed.username, expiresAt: parsed.expiresAt };
  } catch {
    return null;
  }
};

const clearDeviceKeys = (storage: BrowserStorage, clientId: string, username: string): void => {
  cognitoDeviceKeys(clientId, username).forEach((key) => storage.removeItem(key));
};

export const prepareTrustedDevice = (
  storage: BrowserStorage,
  clientId: string,
  email: string,
  now = Date.now()
): boolean => {
  const key = trustKey(clientId, email);
  const raw = storage.getItem(key);
  const record = parseRecord(raw);
  if (record && record.expiresAt > now) return true;

  if (record) clearDeviceKeys(storage, clientId, record.username);
  storage.removeItem(key);
  return false;
};

export const rememberTrustedDevice = (
  storage: BrowserStorage,
  clientId: string,
  email: string,
  username: string,
  now = Date.now()
): number => {
  const expiresAt = now + TRUSTED_DEVICE_MS;
  storage.setItem(trustKey(clientId, email), JSON.stringify({ username, expiresAt }));
  return expiresAt;
};

export const forgetTrustedDevice = (
  storage: BrowserStorage,
  clientId: string,
  email: string,
  username?: string
): void => {
  const key = trustKey(clientId, email);
  const record = parseRecord(storage.getItem(key));
  const storedUsername = username || record?.username;
  if (storedUsername) clearDeviceKeys(storage, clientId, storedUsername);
  storage.removeItem(key);
};

